import React, { useMemo, useState, useCallback, memo } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  Dot,
} from 'recharts';
import { EnergyLogRecord } from '../../../hooks/useHistoricalData';

interface PowerHistoryChartProps {
  data: EnergyLogRecord[];
  loading?: boolean;
  loggingInterval?: number;
  availableDates?: string[];
  selectedDate?: string | null;
  onDateChange?: (date: string | null) => void;
}

// Custom dot that's always visible
const CustomDot = memo((props: any) => {
  const { cx, cy, stroke, dataKey, payload } = props;
  if (!cx || !cy || payload?.isGap) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill={stroke}
      stroke={stroke}
      strokeWidth={1}
    />
  );
});

// Active dot (on hover)
const ActiveDot = memo((props: any) => {
  const { cx, cy, stroke } = props;
  if (!cx || !cy) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill={stroke}
      stroke="#fff"
      strokeWidth={2}
    />
  );
});

// Custom tooltip - memoized
const CustomTooltip = memo(({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  if (dataPoint?.isGap) {
    return (
      <div className="bg-slate-800 border border-red-500 rounded-lg p-3 shadow-lg">
        <p className="text-red-400 text-sm font-semibold">⚠ Data Gap</p>
        <p className="text-slate-400 text-xs">Logging was interrupted</p>
      </div>
    );
  }

  const date = new Date(label);
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
      <p className="text-slate-300 text-sm mb-2">
        {date.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </p>
      {payload.map((entry: any, index: number) => {
        if (entry.value === null || entry.value === undefined) return null;
        return (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value.toFixed(1)} kW</span>
          </p>
        );
      })}
    </div>
  );
});

// Main chart component
export const PowerHistoryChart = memo(function PowerHistoryChart({
  data,
  loading,
  loggingInterval = 5,
  availableDates = [],
  selectedDate = null,
  onDateChange
}: PowerHistoryChartProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Calculate time range based on selected date or today
  const timeRange = useMemo(() => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);
      return { start: start.getTime(), end: end.getTime() };
    } else {
      const now = Date.now();
      const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
      return { start: twentyFourHoursAgo, end: now };
    }
  }, [selectedDate]);

  // Transform data for the chart
  const { chartData, yDomain, hasGaps } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], yDomain: [0, 10] as [number, number], hasGaps: false };
    }

    const sortedData = [...data].sort((a, b) => a.timestamp - b.timestamp);
    const maxGapMs = loggingInterval * 60 * 1000 * 2.5;

    const result: any[] = [];
    let minValue = 0;
    let maxValue = 0;
    let gapCount = 0;

    for (let i = 0; i < sortedData.length; i++) {
      const record = sortedData[i];
      const prevRecord = i > 0 ? sortedData[i - 1] : null;

      // Check for gaps
      if (prevRecord && (record.timestamp - prevRecord.timestamp) > maxGapMs) {
        const gapMidpoint = prevRecord.timestamp + (record.timestamp - prevRecord.timestamp) / 2;
        result.push({
          time: gapMidpoint,
          solar: null,
          load: null,
          grid: null,
          curtailed: null,
          isGap: true,
        });
        gapCount++;
      }

      const solar = Math.round(record.solarPower / 100) / 10;
      const load = Math.round(record.loadPower / 100) / 10;
      const grid = Math.round(record.gridPower / 100) / 10;
      const curtailed = Math.round(record.curtailedPower / 100) / 10;

      maxValue = Math.max(maxValue, solar, load, Math.abs(grid), curtailed);
      minValue = Math.min(minValue, grid);

      result.push({
        time: record.timestamp,
        solar,
        load,
        grid,
        curtailed,
        isGap: false,
      });
    }

    const padding = maxValue * 0.1;
    const yMax = Math.ceil((maxValue + padding) / 5) * 5;
    const yMin = minValue < 0 ? Math.floor((minValue - padding) / 5) * 5 : 0;

    return {
      chartData: result,
      yDomain: [yMin, Math.max(yMax, 10)] as [number, number],
      hasGaps: gapCount > 0
    };
  }, [data, loggingInterval]);

  // X-axis ticks every 3 hours
  const xAxisTicks = useMemo(() => {
    const ticks: number[] = [];
    const threeHoursMs = 3 * 60 * 60 * 1000;

    const startDate = new Date(timeRange.start);
    startDate.setMinutes(0, 0, 0);
    const startHour = startDate.getHours();
    const nearestThreeHour = Math.floor(startHour / 3) * 3;
    startDate.setHours(nearestThreeHour);

    let tickTime = startDate.getTime();
    while (tickTime <= timeRange.end) {
      if (tickTime >= timeRange.start) {
        ticks.push(tickTime);
      }
      tickTime += threeHoursMs;
    }
    return ticks;
  }, [timeRange]);

  const formatTime = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDisplayDate = useCallback((dateStr: string | null) => {
    if (!dateStr) return 'Today (Live)';
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }, []);

  const handleDateSelect = useCallback((date: string | null) => {
    onDateChange?.(date);
    setShowDatePicker(false);
  }, [onDateChange]);

  const closeDatePicker = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const toggleDatePicker = useCallback(() => {
    setShowDatePicker(prev => !prev);
  }, []);

  if (loading) {
    return (
      <div className="bg-card-bg border border-slate-700 rounded-lg p-4 h-80 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-solar border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-400 text-sm">Loading chart data...</p>
        </div>
      </div>
    );
  }

  const noDataMessage = (!data || data.length === 0) ? (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <div className="text-center bg-slate-900/80 rounded-lg px-4 py-3">
        <p className="text-slate-400 text-sm">No data {selectedDate ? 'for this date' : 'yet'}</p>
        {!selectedDate && <p className="text-slate-500 text-xs">Enable logging in Settings &gt; Database</p>}
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-card-bg border border-slate-700 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
            Power History
          </h3>

          {/* Date selector */}
          {onDateChange && (
            <div className="relative">
              <button
                onClick={toggleDatePicker}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-300 hover:border-slate-500 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{formatDisplayDate(selectedDate)}</span>
                <svg className={`w-4 h-4 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDatePicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={closeDatePicker} />
                  <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-20 min-w-[200px] max-h-64 overflow-y-auto">
                    <button
                      onClick={() => handleDateSelect(null)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 transition-colors flex items-center gap-2 ${!selectedDate ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Today (Live)
                    </button>

                    {availableDates.length > 0 && (
                      <div className="border-t border-slate-700">
                        <div className="px-4 py-2 text-xs text-slate-500 uppercase">Historical</div>
                        {[...availableDates].reverse().slice(0, 14).map((date) => {
                          const isToday = date === new Date().toISOString().split('T')[0];
                          if (isToday) return null;
                          return (
                            <button
                              key={date}
                              onClick={() => handleDateSelect(date)}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 transition-colors ${selectedDate === date ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
                            >
                              {formatDisplayDate(date)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {hasGaps && (
            <span className="text-xs text-red-400">⚠ Has gaps</span>
          )}
          <span className="text-xs text-slate-500">
            {data?.length || 0} points
          </span>
        </div>
      </div>

      <div className="h-72 relative">
        {noDataMessage}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={[timeRange.start, timeRange.end]}
              ticks={xAxisTicks}
              tickFormatter={formatTime}
              stroke="#6b7280"
              tick={{ fontSize: 11 }}
              allowDataOverflow={false}
            />
            <YAxis
              stroke="#6b7280"
              tick={{ fontSize: 11 }}
              domain={yDomain}
              tickFormatter={(v) => `${v}`}
              label={{
                value: 'kW',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 11 },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '10px' }}
              formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
            />
            <ReferenceLine y={0} stroke="#4b5563" strokeDasharray="3 3" />

            <Line
              type="monotone"
              dataKey="solar"
              name="Solar"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<ActiveDot />}
              connectNulls={false}
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="load"
              name="Load"
              stroke="#f97316"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<ActiveDot />}
              connectNulls={false}
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="grid"
              name="Grid"
              stroke="#22c55e"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<ActiveDot />}
              connectNulls={false}
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="curtailed"
              name="Curtailed"
              stroke="#ef4444"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={<CustomDot />}
              activeDot={<ActiveDot />}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default PowerHistoryChart;
